import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Select,
  Spin,
  Table,
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { faCodeBranch } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import GlobalAlert2 from "../../home/GlobalAlert2";
import FlatbufferMessagePicker from "../../components/select/FlatbufferMessagePicker";
// WIZARD COMPONENTS
import WizardExportButton from "../components/WizardExportButton";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";
import PayloadEditor from "./components/PayloadEditor";
// INTERFACES
import type { FbMessageRef } from "../../../interfaces/FbMessage.interface";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardStep } from "../utils/wizardInvalidation";
import { describeError } from "../utils/describeError";
// AUTH
import settleInTurn from "../utils/settleInTurn";
import GETJwtToken from "../../../redux/authentication/authentication";
// REDUX
import { useLazyGetChannelsByServerQuery } from "../../../redux/channels/channelAPI";
import { useLazyGetBanksByServerQuery } from "../../../redux/banks/bankAPI";
import { useLazyGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
import {
  useDeleteChannelModifierMutation,
  useLazyGetChannelModifiersByChannelQuery,
  usePostChannelModifierMutation,
  usePutChannelModifierMutation,
} from "../../../redux/channelModifiers/channelModifierAPI";
import {
  useLazyGetDraftQuery,
  useUpsertDraftMutation,
} from "../../../redux/wizardDrafts/wizardDraftsAPI";
// FLATBUFFER ENUMS
import { ConditionOp } from "../../../flatbuffers/generated/archive/condition-op";
import { ConflictPolicy } from "../../../flatbuffers/generated/archive/conflict-policy";
import { ModifierType } from "../../../flatbuffers/generated/archive/modifier-type";
import { ValueTransformMode } from "../../../flatbuffers/generated/archive/value-transform-mode";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { ActiveState } from "../../../flatbuffers/generated/archive/active-state";
import { SequenceStepScope } from "../../../flatbuffers/generated/archive/sequence-step-scope";
import { ModifierTimeoutPolicy } from "../../../flatbuffers/generated/archive/modifier-timeout-policy";
import { ModifierDropAction } from "../../../flatbuffers/generated/archive/modifier-drop-action";
import { ModifierDirection } from "../../../flatbuffers/generated/archive/modifier-direction";
// SCHEMA v7 RULES — pure helpers so the form's rules are testable on their own.
import {
  addLeafMapping,
  cdoLeafIndex,
  defaultStepScope,
  effectiveV7Fields,
  enumMember,
  isPayloadActiveVisible,
  isSequenceVisible,
  isTimeoutPolicyVisible,
  leafKey,
  mappingsAmbiguous,
  observeChannelOptions,
  stepLeafCollision,
  withDirection,
  type LeafMapping,
  type LeafMappings,
} from "./channelModifierV7";
import {
  buildMessageT,
  defaultPayloadDraft,
  extractLeafValueFromPayload,
  isSupportedTargetMessageType,
  payloadDraftFromLeaf,
  targetMessageHasOperatorInputs,
  type PayloadDraft,
} from "../../../flatbuffers/builders/channelModifierPayloadBuilder";
// UUID HELPERS
import { uuidToString, ZERO_UUID_STRING } from "../../../utils/uuid";
import type { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
// FBS CATEGORY KEY HELPERS — for parsing bank.offsetMappingJson keys
// like "control:normal:32" into MessageContent + messageValue pairs.
import { categoryKeyToMessageContent } from "../../../flatbuffers/types/ln2MessageOptions";
// Binary CDO fetch — Modbus and Sequent adapters store their leaf-level
// mappings in a binary FlatBuffer CDO rather than the LN1-style keys table,
// so we fetch it separately (and index it with `cdoLeafIndex`) to know which
// `Control::EnergizeCarCall` / `Traffic::CarCall` / etc. messages the adapter
// actually has mapped.
import { fetchFlatBufferBinary } from "../../../utils/flatbufferApi";

// ---- Types ------------------------------------------------------------------

interface ConditionRow {
  key: string;
  fieldPath: string;
  op: ConditionOp;
  valueText: string;
}

interface ActionRow {
  key: string;
  fieldPath: string;
  newValueText: string;
}

interface SequenceStepRow {
  messageRef: FbMessageRef | null; // expected message leaf (picker)
  timeoutMs: number; // per-step receive timeout in ms
  // Schema v7: the state the observed message must change TO (UNSET = either
  // edge), and how its floor (and car) must agree with the payload's.
  expectedActive: ActiveState;
  scope: SequenceStepScope;
}

/** A stored step, as the modifier list response carries it. */
interface StoredSequenceStep {
  expectedMessageContent: number;
  expectedMessageValue: number;
  timeoutMs: number;
  expectedActive: ActiveState;
  scope: SequenceStepScope;
}

interface ModifierFormState {
  name: string;
  sourceChannelId: string;
  targetChannelId: string;
  modifierType: ModifierType;
  // Leaf-level FlatBuffer message ref picked from the source-side
  // FlatbufferMessagePicker — `Control::EnergizeCarCall`, etc. We mirror
  // `ref.messageContent` into the wire format's `sourceMessageType` so
  // the backend sees the category; the leaf value drives future condition
  // generation. The picker stays disabled until the operator picks a
  // source channel (matches the target-side gating).
  sourceMessageRef: FbMessageRef | null;
  targetMessageType: MessageContent;
  // Leaf-level FlatBuffer message ref picked from the target-side picker.
  // `messageContent` is mirrored into `targetMessageType` for the wire
  // schema; `messageValue` (and friends) drive the payload draft so the
  // operator picks (e.g.) `Control::EnergizeCarCall` once, instead of
  // picking the category and then re-picking the leaf in the
  // PayloadEditor.
  targetMessageRef: FbMessageRef | null;
  priority: number;
  conditions: ConditionRow[];
  actions: ActionRow[];
  payload: PayloadDraft;
  active: boolean;
  // Schema v3: value-transform directive.
  //   PASSTHROUGH    — target gets source value unchanged (default).
  //   SPECIFIC       — only fire when source value is in [start, end);
  //                    target gets source value unchanged.
  //   TRANSFORMATIVE — target_value = source_value + offset.
  valueTransformMode: ValueTransformMode;
  valueRangeStart: number;
  valueRangeEnd: number;
  valueTransformOffset: number;
  // Schema v5: ordered expected-message sequence for BEFORE modifiers.
  // After the modifier payload is dispatched on the target channel, each
  // step must be observed on the target channel's inbound LN2 stream before
  // the source message is released. Empty = legacy ack-only behavior.
  expectedSequence: SequenceStepRow[];
  // Schema v6: conflict resolution policy. Governs how this modifier resolves
  // with others emitting to the same target channel + target message.
  // COEXIST (0) = default, preserves pre-v6 all-fire behavior.
  conflictPolicy: ConflictPolicy;
  // Schema v7. OUTBOUND fires on commands going to the source vendor; INBOUND
  // fires on state changes the source vendor reports, and is AFTER-only.
  direction: ModifierDirection;
  // Channel the expected steps are observed on; "" = the target channel.
  observeChannelId: string;
  // The `active` flag stamped on the payload; UNSET = the legacy active:true.
  payloadActive: ActiveState;
  // What a failed BEFORE gate does with the held message: RELEASE (fail open)
  // or DROP (fail closed, reported to the operator as Withheld).
  timeoutPolicy: ModifierTimeoutPolicy;
  // After a DROP: NONE, or REVERT (re-send the payload with active inverted).
  dropAction: ModifierDropAction;
}

interface ChannelOption {
  id: string;
  name: string;
  serverId: string;
}

interface ModifierRow {
  id: string;
  name: string;
  sourceChannelId: string;
  sourceChannelName: string;
  targetChannelId: string;
  targetChannelName: string;
  modifierType: ModifierType;
  priority: number;
  active: boolean;
  // Schema v3 — surfaced in the table so the user can see at a glance
  // which modifiers transform values vs. forward them.
  valueTransformMode: ValueTransformMode;
  valueRangeStart: number;
  valueRangeEnd: number;
  valueTransformOffset: number;
  // Edit-side restoration data. The list response already carries every
  // field needed to rehydrate the form on Edit; preserving these here
  // saves an extra GET /channel_modifiers/{id} per click. The picker
  // shape requires the leaf enum value, which only lives inside the
  // payload tree — we extract it once at load time and stash the result
  // alongside the category discriminant.
  sourceMessageType: MessageContent;
  // Schema v4 — exact source leaf value persisted via the new scalar
  // pair. Restored into the source picker ref on handleEdit so the
  // operator sees their original selection rather than a zero placeholder.
  sourceMessageValue: number;
  targetMessageType: MessageContent;
  targetMessageValue: number | null;
  // Schema v5: stored steps read from the API response and rehydrated into
  // SequenceStepRow[] during handleEdit (v7 adds expectedActive and scope).
  expectedSequence: StoredSequenceStep[];
  // IN_FLIGHT rule set read from the API response and rehydrated into the
  // conditions/actions sub-forms during handleEdit. Previously dropped, which
  // wiped an IN_FLIGHT modifier's rules whenever it was edited and re-saved.
  conditions: ConditionRow[];
  actions: ActionRow[];
  // Schema v6: conflict policy read from the API response and rehydrated
  // into the form on handleEdit.
  conflictPolicy: ConflictPolicy;
  // Schema v7 fields, rehydrated into the form on handleEdit.
  direction: ModifierDirection;
  observeChannelId: string;
  payloadActive: ActiveState;
  timeoutPolicy: ModifierTimeoutPolicy;
  dropAction: ModifierDropAction;
}

const EMPTY_FORM: ModifierFormState = {
  name: "",
  sourceChannelId: "",
  targetChannelId: "",
  modifierType: ModifierType.BEFORE,
  // Both pickers start cleared — the operator must pick the source
  // channel + leaf before the source picker accepts a message, and the
  // target channel + leaf before the target picker fires.
  sourceMessageRef: null,
  targetMessageType: MessageContent.Status_StatusMessage,
  targetMessageRef: null,
  priority: 0,
  conditions: [],
  actions: [],
  payload: defaultPayloadDraft(MessageContent.Status_StatusMessage),
  active: true,
  valueTransformMode: ValueTransformMode.PASSTHROUGH,
  valueRangeStart: 0,
  valueRangeEnd: 0,
  valueTransformOffset: 0,
  expectedSequence: [],
  conflictPolicy: ConflictPolicy.COEXIST,
  direction: ModifierDirection.OUTBOUND,
  observeChannelId: "",
  payloadActive: ActiveState.UNSET,
  timeoutPolicy: ModifierTimeoutPolicy.RELEASE,
  dropAction: ModifierDropAction.NONE,
};

/**
 * A step row from a stored draft, whatever version of the form wrote it. Drafts
 * saved before v7 carry no `expectedActive` / `scope`; they read as the pre-v7
 * behaviour (either edge, same floor).
 */
function normalizeStepRow(raw: Partial<SequenceStepRow>): SequenceStepRow {
  return {
    messageRef: raw.messageRef ?? null,
    timeoutMs: typeof raw.timeoutMs === "number" ? raw.timeoutMs : 2000,
    expectedActive: enumMember(ActiveState, raw.expectedActive, ActiveState.UNSET),
    scope: enumMember(SequenceStepScope, raw.scope, SequenceStepScope.SAME_POSITION),
  };
}

/** Channel id or "" — the all-zero UUID an unset reference unpacks to reads as "". */
function optionalChannelId(raw: unknown): string {
  const id = uuidToString((raw ?? null) as UuidT | string | null);
  return id === ZERO_UUID_STRING ? "" : id;
}

/**
 * Maps a raw FlatBuffer-unpacked modifier record (camelCase or snake_case)
 * into a typed `ModifierRow`. Exported for unit testing; the component's
 * `loadAllModifiers` callback calls this for each element of the API response.
 *
 * @param m        - Raw record from the list response (shape varies by version).
 * @param channels - Current channel option list used for name lookup.
 */
export function mapModifierRow(
  m: Record<string, unknown>,
  channels: { id: string; name: string }[],
): ModifierRow {
  const modifierId = uuidToString(m.id as UuidT | string | null);
  const srcId = uuidToString((m.sourceChannelId ?? m.source_channel_id) as UuidT | string | null);
  const tgtId = uuidToString((m.targetChannelId ?? m.target_channel_id) as UuidT | string | null);
  const valueModeRaw = m.valueTransformMode ?? m.value_transform_mode;
  const valueMode =
    typeof valueModeRaw === "number"
      ? (valueModeRaw as ValueTransformMode)
      : ValueTransformMode.PASSTHROUGH;
  // Prefer the v4 scalar `sourceMessageContent` over the union discriminant.
  // Older rows will have 0 in `sourceMessageContent` and fall back to the
  // union discriminant so the category is never silently lost.
  const sourceMessageContentRaw = Number(m.sourceMessageContent ?? 0);
  const sourceMessageTypeFromUnion = (m.sourceMessageTypeType ??
    m.source_message_type ??
    MessageContent.NONE) as MessageContent;
  const sourceMessageType =
    sourceMessageContentRaw !== 0
      ? (sourceMessageContentRaw as MessageContent)
      : sourceMessageTypeFromUnion;
  const sourceMessageValue = Number(m.sourceMessageValue ?? 0);
  // The payload names the exact target leaf, so it wins. Without one (IN_FLIGHT,
  // ControllerInfo), prefer the scalar `targetMessageContent` over the union
  // discriminant, as for the source. A server that predates the scalar sends 0,
  // and the union fallback still applies.
  const targetMessageContentRaw = Number(m.targetMessageContent ?? 0);
  const targetMessageTypeFromUnion = (m.targetMessageTypeType ??
    m.target_message_type ??
    MessageContent.NONE) as MessageContent;
  const targetMessageTypeFromField =
    targetMessageContentRaw !== 0
      ? (targetMessageContentRaw as MessageContent)
      : targetMessageTypeFromUnion;
  const leafFromPayload = extractLeafValueFromPayload(m.payload);
  const targetMessageType = leafFromPayload?.messageContent ?? targetMessageTypeFromField;
  const targetMessageValue = leafFromPayload?.messageValue ?? null;
  return {
    id: modifierId,
    name: String(m.name ?? ""),
    sourceChannelId: srcId,
    sourceChannelName: channels.find((c) => c.id === srcId)?.name ?? srcId,
    targetChannelId: tgtId,
    targetChannelName: channels.find((c) => c.id === tgtId)?.name ?? tgtId,
    modifierType: (m.modifierType ?? m.modifier_type ?? ModifierType.BEFORE) as ModifierType,
    priority: Number(m.priority ?? 0),
    active: Boolean(m.active ?? true),
    valueTransformMode: valueMode,
    valueRangeStart: Number(m.valueRangeStart ?? m.value_range_start ?? 0),
    valueRangeEnd: Number(m.valueRangeEnd ?? m.value_range_end ?? 0),
    valueTransformOffset: Number(m.valueTransformOffset ?? m.value_transform_offset ?? 0),
    sourceMessageType,
    sourceMessageValue,
    targetMessageType,
    targetMessageValue,
    // Steps in either shape: camelCase (Object-API unpack) or snake_case (raw
    // JSON). v7's expectedActive / scope default to the pre-v7 meaning.
    expectedSequence: (
      (m.expectedSequence ?? m.expected_sequence ?? []) as Array<Record<string, unknown>>
    ).map((s) => ({
      expectedMessageContent: Number(s.expectedMessageContent ?? s.expected_message_content ?? 0),
      expectedMessageValue: Number(s.expectedMessageValue ?? s.expected_message_value ?? 0),
      timeoutMs: Number(s.timeoutMs ?? s.timeout_ms ?? 2000),
      expectedActive: enumMember(
        ActiveState,
        s.expectedActive ?? s.expected_active,
        ActiveState.UNSET,
      ),
      scope: enumMember(SequenceStepScope, s.scope, SequenceStepScope.SAME_POSITION),
    })),
    // IN_FLIGHT rules: read both camelCase (Object-API unpack) and snake_case
    // (raw JSON) shapes so an edited IN_FLIGHT modifier keeps its rule set.
    conditions: ((m.conditions ?? []) as Array<Record<string, unknown>>).map((c, i) => ({
      key: `cond-${i}`,
      fieldPath: String(c.fieldPath ?? c.field_path ?? ""),
      op: Number(c.op ?? 0) as ConditionOp,
      valueText: String(c.valueText ?? c.value_text ?? ""),
    })),
    actions: ((m.actions ?? []) as Array<Record<string, unknown>>).map((a, i) => ({
      key: `act-${i}`,
      fieldPath: String(a.fieldPath ?? a.field_path ?? ""),
      newValueText: String(a.newValueText ?? a.new_value_text ?? ""),
    })),
    conflictPolicy: Number(m.conflictPolicy ?? m.conflict_policy ?? 0) as ConflictPolicy,
    // Schema v7. Absent fields (pre-v7 rows) read as their pre-v7 meaning, and
    // an unset observe channel — absent, null, or the zero UUID — reads as "".
    direction: enumMember(ModifierDirection, m.direction, ModifierDirection.OUTBOUND),
    observeChannelId: optionalChannelId(m.observeChannelId ?? m.observe_channel_id),
    payloadActive: enumMember(ActiveState, m.payloadActive ?? m.payload_active, ActiveState.UNSET),
    timeoutPolicy: enumMember(
      ModifierTimeoutPolicy,
      m.timeoutPolicy ?? m.timeout_policy,
      ModifierTimeoutPolicy.RELEASE,
    ),
    dropAction: enumMember(
      ModifierDropAction,
      m.dropAction ?? m.drop_action,
      ModifierDropAction.NONE,
    ),
  };
}

/**
 * Build the short human-readable summary that lands in the modifier
 * table's Value Mode column — "Passthrough", "Specific [3, 5)", or
 * "Transformative +/-N" — so the user can see at a glance how each
 * row transforms the source value without opening the editor.
 */
function valueModeSummary(row: ModifierRow): string {
  switch (row.valueTransformMode) {
    case ValueTransformMode.SPECIFIC:
      return `Specific [${row.valueRangeStart}, ${row.valueRangeEnd})`;
    case ValueTransformMode.TRANSFORMATIVE: {
      const sign = row.valueTransformOffset >= 0 ? "+" : "";
      return `Transformative ${sign}${row.valueTransformOffset}`;
    }
    case ValueTransformMode.PASSTHROUGH:
    default:
      return "Passthrough";
  }
}

/**
 * Walk a bank's `offsetMappingJson` and return the set of
 * `"messageContent:messageValue"` keys mapped on any of its lifts. The
 * field is an array (one entry per lift) of `Record<compositeKey, offset>`
 * where compositeKey looks like `"control:normal:32"` or
 * `"traffic:normal:14"`. We split the key into category + leaf,
 * resolve the category via `categoryKeyToMessageContent`, and build the
 * canonical `"<mc>:<mv>"` form that `FlatbufferMessagePicker`'s
 * `messageValueFilter` prop expects.
 *
 * Returns an empty array when the bank carries no offset mapping (or
 * the field arrived in an unexpected shape). Callers union these into a
 * per-channel filter; an empty extraction does NOT collapse the filter.
 */
function extractBankOffsetMappingKeys(bank: Record<string, unknown>): string[] {
  const raw = bank.offsetMappingJson ?? bank.offset_mapping;
  if (!raw) return [];
  let mappings: Record<string, unknown>[];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    mappings = parsed as Record<string, unknown>[];
  } catch {
    return [];
  }
  const out: string[] = [];
  const seen = new Set<string>();
  mappings.forEach((liftMap) => {
    if (!liftMap || typeof liftMap !== "object") return;
    Object.keys(liftMap).forEach((compositeKey) => {
      const parts = compositeKey.split(":");
      if (parts.length < 3) return;
      const categoryKey = parts[0]!;
      const leafValue = Number(parts[2]);
      if (!Number.isFinite(leafValue)) return;
      const mc = categoryKeyToMessageContent(categoryKey);
      if (mc === MessageContent.NONE) return;
      const composite = `${mc}:${leafValue}`;
      if (seen.has(composite)) return;
      seen.add(composite);
      out.push(composite);
    });
  });
  return out;
}

const MODIFIER_TYPE_LABEL: Record<ModifierType, string> = {
  [ModifierType.BEFORE]: "Before",
  [ModifierType.AFTER]: "After",
  [ModifierType.IN_FLIGHT]: "InFlight",
};

// Target message picking is delegated to `FlatbufferMessagePicker`, which
// surfaces leaf-level options (e.g. `Control::EnergizeCarCall`,
// `Traffic::CarCall`) grouped by category. The operator picks the actual
// FlatBuffer message they want the modifier to emit, mirroring the
// adapter-mapping editor used everywhere else in the wizard, instead of
// the previous category-only choice that left the leaf to be re-picked
// inside the PayloadEditor.
//
// Picker options ARE filtered by what the selected channel's adapter has
// mapped, via `buildChannelMessageFilter` — the union of LN1-style adapter
// keys, CDO-template (`register_offsets`) mappings, and bank offset
// mappings. (An older revision dropped filtering entirely because the
// `/keys/?adapter_id=` endpoint missed CDO-template adapters; the union
// filter closed that gap, so filtering is back for the source, target,
// AND expected-sequence pickers.) The expected-sequence picker uses the
// OBSERVE channel's filter — the target channel unless the modifier names
// another (schema v7): a sequence step is a message VTS must observe on that
// channel, so only messages its adapter can actually map are meaningful
// choices. The confirmation is configuration, not a reserved message: it is
// whatever the observe channel's adapter maps the interlock's input to.

// ---- Validation -------------------------------------------------------------

function validateForm(
  form: ModifierFormState,
  channelServerMap: Record<string, string>,
): string | null {
  if (!form.name.trim()) return "Name is required";
  if (!form.sourceChannelId) return "Source channel is required";
  if (!form.targetChannelId) return "Target channel is required";
  if (form.sourceChannelId === form.targetChannelId)
    return "Source and target channels must be different";
  const srcServer = channelServerMap[form.sourceChannelId];
  const tgtServer = channelServerMap[form.targetChannelId];
  if (srcServer && tgtServer && srcServer !== tgtServer)
    return "Source and target channels must belong to the same server";
  // BEFORE/AFTER modifiers must specify both the inbound source message
  // that triggers them AND the concrete leaf they dispatch — leaving
  // either as a category-only wildcard makes runtime behaviour ambiguous.
  // IN_FLIGHT modifiers mutate in place and don't dispatch, so they can
  // skip the target leaf requirement.
  if (form.sourceMessageRef === null) return "Source message is required";
  if (form.modifierType !== ModifierType.IN_FLIGHT && form.targetMessageRef === null)
    return "Target message is required";
  return null;
}

/**
 * The note a step shows when it waits for the same message as the modifier's
 * source or payload. Allowed: a step counts only a change the observe channel
 * reports after the payload write starts, so a device echoing the commanded
 * state back is a real confirmation.
 */
function stepCollisionText(collision: "source" | "payload"): string {
  return collision === "source"
    ? "This step waits for the same message as the modifier's source. That is fine when the observed device reports it back; only a change reported after the payload is written counts."
    : "This step waits for the same message the modifier sends. That is fine when the device confirms by reporting the commanded state back; only a change reported after the payload is written counts.";
}

// ---- Component --------------------------------------------------------------

export default function StepChannelModifiers(): React.JSX.Element {
  const { state: wizardState, setState: setWizardState, markStepCompleted } = useWizard();
  const [t] = useTranslation();
  const { serverId } = wizardState;

  // Channel list for source/target selects
  const [triggerGetChannels] = useLazyGetChannelsByServerQuery();
  const [channels, setChannels] = useState<ChannelOption[]>([]);

  // Auto-mark this step complete whenever the operator is on it.
  // Channel modifiers are optional — most orgs never use them — and
  // Next must work without requiring the operator to either type a
  // modifier or hunt for a Skip button. Real modifier saves still
  // fire `markStepCompleted` through `handleSave`; this just
  // guarantees the completion gate isn't a wall for operators who
  // have nothing to add. Depending on `currentStep` re-fires this if
  // an upstream step unmarked us.
  useEffect(() => {
    if (wizardState.currentStep === WizardStep.ChannelModifiers) {
      markStepCompleted(WizardStep.ChannelModifiers);
    }
  }, [wizardState.currentStep, markStepCompleted]);

  // Bank → channel/adapter linkage. The Bank step (positioned before
  // Channel Modifiers in the wizard) is where the operator binds a
  // channel and an adapter together — we use that binding to figure out
  // which leaf messages an adapter knows about, then filter the source
  // and target message pickers down to that set.
  //
  // `channelAdapters` maps a channel id to the list of adapters it is
  // bound to via any bank. `bankMessageKeys` maps a channel id to the
  // union of leaf-message keys harvested from the bank's
  // `offsetMappingJson` (covers Modbus and any offset-based adapter
  // configured via the offset-matrix UI). `adapterMessageKeys` maps an
  // adapter id to the leaf-message keys harvested from the dedicated
  // `/keys/?filter_target=adapter_id` endpoint (covers LN1-style
  // adapters whose mappings live in the keys table).
  //
  // Leaf-message keys are stored as the same `"messageContent:messageValue"`
  // string the `FlatbufferMessagePicker` consumes via its
  // `messageValueFilter` prop.
  const [triggerGetBanks] = useLazyGetBanksByServerQuery();
  const [triggerGetAdapterKeys] = useLazyGetAdapterKeysQuery();
  const [channelAdapters, setChannelAdapters] = useState<Record<string, string[]>>({});
  const [bankMessageKeys, setBankMessageKeys] = useState<Record<string, Set<string>>>({});
  const [adapterMessageKeys, setAdapterMessageKeys] = useState<Record<string, Set<string>>>({});
  // Modbus-specific: leaf mappings parsed from each adapter's binary
  // CDO template (the `/adapters/{id}/cdo` endpoint). Keyed by adapter
  // id; value is a Set of `"messageContent:messageValue"` strings. We
  // fetch every adapter speculatively — non-Modbus adapters either
  // return a non-Modbus template (caught by the try/catch around
  // `unpackModbusTemplate`) or `null` (caught by the early-return);
  // either way the cache resolves to an empty Set so we don't retry.
  const [adapterCdoMessageKeys, setAdapterCdoMessageKeys] = useState<Record<string, Set<string>>>(
    {},
  );
  // Every mapping of every leaf, per adapter, from the two sources that list
  // mappings one by one (binary CDO pins/registers and keys-table rows). Used
  // only to warn when an expected step's confirmation leaf is mapped more
  // than once on the observe channel in a way its scope cannot tell apart.
  const [adapterCdoLeafMappings, setAdapterCdoLeafMappings] = useState<
    Record<string, LeafMappings>
  >({});
  const [adapterKeyLeafMappings, setAdapterKeyLeafMappings] = useState<
    Record<string, LeafMappings>
  >({});
  // Most lifts any bank on a channel has — decides a new step's default scope.
  const [channelMaxLifts, setChannelMaxLifts] = useState<Record<string, number>>({});

  // Modifier CRUD
  const [triggerGetModifiers] = useLazyGetChannelModifiersByChannelQuery();
  const [
    postModifier,
    {
      isLoading: isPosting,
      isError: isErrorPost,
      isSuccess: isSuccessPost,
      error: errorPost,
      reset: resetPost,
    },
  ] = usePostChannelModifierMutation();
  const [putModifier, { isLoading: isPutting }] = usePutChannelModifierMutation();
  const [deleteModifier] = useDeleteChannelModifierMutation();

  const [modifiers, setModifiers] = useState<ModifierRow[]>([]);
  const [form, setForm] = useState<ModifierFormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const access = useCurrentWizardStepAccess();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  // Draft persistence — when the operator navigates away from this step,
  // or reloads the browser, their in-progress selections (source channel,
  // target channel, partial form fields) used to vanish because every
  // wizard step held its form in local React state alone. The page would
  // come back blank and the saved modifiers wouldn't even show until the
  // operator re-picked the source channel.
  //
  // Mirrors the pattern `StepLiftsFloors` uses — write the relevant form
  // slice into the `wizard_drafts` table on every meaningful change,
  // restore on mount. The fixed scope ("channel-modifiers") differentiates
  // this step's draft row from `LiftsFloors` drafts that share the same
  // step number with a per-bank scope.
  const [triggerGetDraft] = useLazyGetDraftQuery();
  const [triggerUpsertDraft] = useUpsertDraftMutation();
  const draftRestoredRef = useRef(false);

  // Synchronous re-entry guard for `handleSave`. RTK Query's `isLoading`
  // flag (and therefore the AntD Button's `loading={isSaving}` prop) only
  // flips to `true` AFTER React processes the mutation's state update,
  // which lags the actual button click. A user double-clicking the
  // "Add Modifier" button within that window can fire several POST
  // requests before the loading guard engages — and because each click
  // reads the same closure of `form`, every duplicate validates,
  // submits, and persists. (Observed: six identical "Modifier Channel"
  // rows created within an 8-second span from a handful of mash-clicks.)
  // This ref flips synchronously inside the handler, so even back-to-back
  // clicks land at most one in-flight save.
  const savingRef = useRef(false);

  // channelServerMap: channelId → serverId for cross-server validation
  const channelServerMap = Object.fromEntries(channels.map((ch) => [ch.id, ch.serverId]));

  // Load channels on mount.
  // The API returns each channel record with typed FlatBuffer-shaped ids:
  //   { id: { value: { upper: BigInt, lower: BigInt } }, serverId: { … }, … }
  // Stringifying that directly produces "[object Object]" — and when every
  // channel collapses to the same string, the source/target filter excludes
  // all of them and the target Select renders empty. Unwrap to canonical
  // UUID strings via `uuidToString` (the same helper [ChannelsDeleteModal.tsx](src/pages/edgeManagement/channels/ChannelsDeleteModal.tsx) uses) before
  // building ChannelOption rows. Field name is `serverId` (camelCase from
  // the wire format), not `server_id`.
  const loadChannels = useCallback(async () => {
    if (!serverId) return;
    try {
      const token = await GETJwtToken();
      const result = await triggerGetChannels({
        serverId,
        page: 1,
        limit: 1000,
        token,
      });
      const items: ChannelOption[] = (
        (result.data?.data ?? result.data ?? []) as Record<string, unknown>[]
      ).map((ch) => {
        const channelId = uuidToString(ch.id as UuidT | string | null);
        const channelServer = uuidToString(ch.serverId as UuidT | string | null);
        return {
          id: channelId,
          name: String(ch.name ?? ""),
          serverId: channelServer !== ZERO_UUID_STRING ? channelServer : (serverId ?? ""),
        };
      });
      setChannels(items);
    } catch {
      setStepError("Failed to load channels");
    }
  }, [serverId, triggerGetChannels]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // Load banks for the server once and index them as channelId → adapterIds.
  // While we're at it, parse each bank's `offsetMappingJson` so we can
  // expose its leaf-level mappings to the message picker filter — that's
  // the offset matrix the operator filled out on the Banks step.
  //
  // The bank API expects `idServer` (not `serverId`) — the prior bug
  // where this was named `serverId` produced
  // `/banks/byServer/undefined → 401` and the entire channel-adapter
  // linkage stayed empty, so the picker filter could never see any
  // mappings.
  const loadBanks = useCallback(async () => {
    if (!serverId) return;
    try {
      const token = await GETJwtToken();
      const result = await triggerGetBanks({
        idServer: serverId,
        page: 1,
        limit: 1000,
        token,
      });
      const rows = (result.data?.data ?? result.data ?? []) as Record<string, unknown>[];
      const nextChannelAdapters: Record<string, string[]> = {};
      const nextBankMessageKeys: Record<string, Set<string>> = {};
      const nextChannelMaxLifts: Record<string, number> = {};
      rows.forEach((b) => {
        const channelId = uuidToString(b.channelId as UuidT | string | null);
        const adapterId = uuidToString(b.adapterId as UuidT | string | null);
        if (channelId && channelId !== ZERO_UUID_STRING) {
          const lifts = Math.max(
            Number(b.numberOfLifts ?? b.number_of_lifts ?? 0) || 0,
            Array.isArray(b.lifts) ? b.lifts.length : 0,
          );
          nextChannelMaxLifts[channelId] = Math.max(nextChannelMaxLifts[channelId] ?? 0, lifts);
        }
        if (
          channelId === ZERO_UUID_STRING ||
          adapterId === ZERO_UUID_STRING ||
          !channelId ||
          !adapterId
        ) {
          return;
        }
        const adapterBucket = nextChannelAdapters[channelId] ?? [];
        if (!adapterBucket.includes(adapterId)) adapterBucket.push(adapterId);
        nextChannelAdapters[channelId] = adapterBucket;

        const messageBucket = nextBankMessageKeys[channelId] ?? new Set<string>();
        extractBankOffsetMappingKeys(b).forEach((key) => messageBucket.add(key));
        nextBankMessageKeys[channelId] = messageBucket;
      });
      setChannelAdapters(nextChannelAdapters);
      setBankMessageKeys(nextBankMessageKeys);
      setChannelMaxLifts(nextChannelMaxLifts);
    } catch {
      // Bank load failures are non-fatal — the pickers fall back to the
      // unfiltered full enum list and the operator can still pick freely.
      setStepError("Failed to load banks for adapter linkage");
    }
  }, [serverId, triggerGetBanks]);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  // Fetch and cache an adapter's binary-CDO leaf mappings. Idempotent — once a
  // non-empty or empty cache entry exists we don't refetch.
  //
  // Modbus first: its template stores each LN2 message as a
  // `"<category>:<variant>:<value>"` string (e.g. `"control:normal:32"`),
  // which `cdoLeafIndex` resolves through `categoryKeyToMessageContent` into
  // the `"mc:mv"` form the picker's `messageValueFilter` prop consumes. When
  // the Modbus unpack fails or maps nothing, the bytes are read as a Sequent
  // `Mapping` through the generated Object API — each pin's
  // `discreteToMessage[].value.msg` gives its leaf, floor and lift — which is
  // what lets an interlock's confirmation input on a Sequent board be picked
  // as an expected step. Anything else (no bytes, a fetch failure, another
  // schema) caches as empty.
  const ensureAdapterCdoMappings = useCallback(
    async (adapterId: string) => {
      if (!adapterId || adapterCdoMessageKeys[adapterId] !== undefined) return;
      const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
      let keys = new Set<string>();
      let mappings: LeafMappings = new Map();
      try {
        const token = await GETJwtToken();
        const bytes = await fetchFlatBufferBinary(`${apiBase}/adapters/${adapterId}/cdo`, token);
        if (bytes && bytes.length > 0) {
          const index = cdoLeafIndex(bytes);
          keys = index.filterKeys;
          mappings = index.mappings;
        }
      } catch {
        // Fetch failure — degrade to an empty set.
        keys = new Set<string>();
        mappings = new Map();
      }
      setAdapterCdoMessageKeys((prev) => ({ ...prev, [adapterId]: keys }));
      setAdapterCdoLeafMappings((prev) => ({ ...prev, [adapterId]: mappings }));
    },
    [adapterCdoMessageKeys],
  );

  // Fetch and cache the keys-table leaf mappings for an adapter the
  // first time we encounter it. Idempotent — repeat calls for an adapter
  // we've already loaded short-circuit immediately.
  const ensureAdapterKeys = useCallback(
    async (adapterId: string) => {
      if (!adapterId || adapterMessageKeys[adapterId] !== undefined) return;
      try {
        const token = await GETJwtToken();
        const result = await triggerGetAdapterKeys({ adapterId, token });
        const rows = (result.data?.data ?? result.data ?? []) as Record<string, unknown>[];
        const keys = new Set<string>();
        const mappings: LeafMappings = new Map();
        rows.forEach((k) => {
          const mc = Number(k.messageContent ?? k.message_content);
          const mv = Number(k.messageValue ?? k.message_value);
          if (Number.isFinite(mc) && Number.isFinite(mv) && mc > 0) {
            keys.add(leafKey(mc, mv));
            // A key row names no single floor or car (floor-based rows fan
            // out), so it counts as a positionless mapping.
            addLeafMapping(mappings, leafKey(mc, mv), { position: null, liftId: "" });
          }
        });
        setAdapterMessageKeys((prev) => ({ ...prev, [adapterId]: keys }));
        setAdapterKeyLeafMappings((prev) => ({ ...prev, [adapterId]: mappings }));
      } catch {
        // Cache an empty Set so we don't retry on every render.
        setAdapterMessageKeys((prev) => ({ ...prev, [adapterId]: new Set<string>() }));
        setAdapterKeyLeafMappings((prev) => ({ ...prev, [adapterId]: new Map() }));
      }
    },
    [adapterMessageKeys, triggerGetAdapterKeys],
  );

  // Trigger BOTH the keys-table fetch (covers LN1-style adapters) and
  // the Modbus CDO template fetch (covers Modbus / register-offset
  // adapters) whenever the picked source/target channels resolve to
  // adapters we haven't loaded yet. Each ensure-callback is idempotent
  // so repeat triggers are cheap.
  useEffect(() => {
    const adapters = new Set<string>();
    if (form.sourceChannelId) {
      (channelAdapters[form.sourceChannelId] ?? []).forEach((a) => adapters.add(a));
    }
    if (form.targetChannelId) {
      (channelAdapters[form.targetChannelId] ?? []).forEach((a) => adapters.add(a));
    }
    // The observe channel's adapters feed the expected-step picker.
    if (form.observeChannelId) {
      (channelAdapters[form.observeChannelId] ?? []).forEach((a) => adapters.add(a));
    }
    adapters.forEach((a) => {
      ensureAdapterKeys(a);
      ensureAdapterCdoMappings(a);
    });
  }, [
    form.sourceChannelId,
    form.targetChannelId,
    form.observeChannelId,
    channelAdapters,
    ensureAdapterKeys,
    ensureAdapterCdoMappings,
  ]);

  // Restore the form state from the server-side wizard draft on mount.
  // Runs once per `organizationId` change. Refs prevent a second restore
  // from clobbering operator edits after the first restore — a stale
  // draft must not override what the operator is typing right now.
  useEffect(() => {
    if (!wizardState.organizationId || draftRestoredRef.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await GETJwtToken();
        const result = await triggerGetDraft({
          orgId: wizardState.organizationId!,
          step: WizardStep.ChannelModifiers,
          scopeId: "channel-modifiers",
          token,
        }).unwrap();
        if (cancelled) return;
        const draft = (result?.data?.draft_data ?? null) as
          (Partial<ModifierFormState> & { sourceMessageRef?: FbMessageRef | null }) | null;
        if (!draft) {
          draftRestoredRef.current = true;
          return;
        }
        setForm((prev) => ({
          ...prev,
          sourceChannelId: draft.sourceChannelId ?? prev.sourceChannelId,
          targetChannelId: draft.targetChannelId ?? prev.targetChannelId,
          modifierType: draft.modifierType ?? prev.modifierType,
          name: draft.name ?? prev.name,
          priority: typeof draft.priority === "number" ? draft.priority : prev.priority,
          active: typeof draft.active === "boolean" ? draft.active : prev.active,
          sourceMessageRef: draft.sourceMessageRef ?? prev.sourceMessageRef,
          targetMessageRef: draft.targetMessageRef ?? prev.targetMessageRef,
          targetMessageType: draft.targetMessageType ?? prev.targetMessageType,
          valueTransformMode: draft.valueTransformMode ?? prev.valueTransformMode,
          valueRangeStart:
            typeof draft.valueRangeStart === "number"
              ? draft.valueRangeStart
              : prev.valueRangeStart,
          valueRangeEnd:
            typeof draft.valueRangeEnd === "number" ? draft.valueRangeEnd : prev.valueRangeEnd,
          valueTransformOffset:
            typeof draft.valueTransformOffset === "number"
              ? draft.valueTransformOffset
              : prev.valueTransformOffset,
          expectedSequence: Array.isArray(draft.expectedSequence)
            ? (draft.expectedSequence as Partial<SequenceStepRow>[]).map(normalizeStepRow)
            : prev.expectedSequence,
          // Schema v7 fields; a draft saved before they existed keeps the
          // pre-v7 defaults.
          direction: enumMember(ModifierDirection, draft.direction, prev.direction),
          observeChannelId:
            typeof draft.observeChannelId === "string"
              ? draft.observeChannelId
              : prev.observeChannelId,
          payloadActive: enumMember(ActiveState, draft.payloadActive, prev.payloadActive),
          timeoutPolicy: enumMember(ModifierTimeoutPolicy, draft.timeoutPolicy, prev.timeoutPolicy),
          dropAction: enumMember(ModifierDropAction, draft.dropAction, prev.dropAction),
          // If the operator had a target leaf saved, regenerate the
          // payload draft from it so the PayloadEditor + value-flow
          // logic see the same shape they would after a fresh pick.
          payload:
            draft.targetMessageRef &&
            typeof draft.targetMessageType === "number" &&
            draft.targetMessageType !== MessageContent.NONE
              ? payloadDraftFromLeaf(draft.targetMessageType, draft.targetMessageRef.messageValue)
              : prev.payload,
        }));
        draftRestoredRef.current = true;
      } catch {
        // No draft yet (404) or transient failure — leave the form at
        // EMPTY_FORM and let the operator start fresh. Still flag as
        // restored so we don't refetch on every render.
        draftRestoredRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wizardState.organizationId, triggerGetDraft]);

  // Auto-save the form to the wizard draft whenever any persistent field
  // changes. Debounced so a burst of keystrokes only produces one POST.
  // We only persist AFTER the initial restore has had a chance to run —
  // otherwise the empty EMPTY_FORM would race the GET and wipe the
  // draft on first mount.
  useEffect(() => {
    if (!wizardState.organizationId || !draftRestoredRef.current) return undefined;
    const timer = setTimeout(async () => {
      try {
        const token = await GETJwtToken();
        triggerUpsertDraft({
          orgId: wizardState.organizationId!,
          step: WizardStep.ChannelModifiers,
          scopeId: "channel-modifiers",
          scopeType: "step",
          // Persist only the fields the operator interacts with — the
          // derived `payload` is rebuilt from `targetMessageRef` on
          // restore, so we skip its bigger nested draft to keep the
          // stored blob small.
          draftData: {
            sourceChannelId: form.sourceChannelId,
            targetChannelId: form.targetChannelId,
            modifierType: form.modifierType,
            name: form.name,
            priority: form.priority,
            active: form.active,
            sourceMessageRef: form.sourceMessageRef,
            targetMessageRef: form.targetMessageRef,
            targetMessageType: form.targetMessageType,
            valueTransformMode: form.valueTransformMode,
            valueRangeStart: form.valueRangeStart,
            valueRangeEnd: form.valueRangeEnd,
            valueTransformOffset: form.valueTransformOffset,
            expectedSequence: form.expectedSequence,
            direction: form.direction,
            observeChannelId: form.observeChannelId,
            payloadActive: form.payloadActive,
            timeoutPolicy: form.timeoutPolicy,
            dropAction: form.dropAction,
          },
          token,
        });
      } catch {
        // Transient draft-save failures are non-fatal — the operator
        // hasn't lost work (it's still in React state) and the next
        // edit retries automatically.
      }
    }, 750);
    return () => clearTimeout(timer);
  }, [
    form.sourceChannelId,
    form.targetChannelId,
    form.modifierType,
    form.name,
    form.priority,
    form.active,
    form.sourceMessageRef,
    form.targetMessageRef,
    form.targetMessageType,
    form.valueTransformMode,
    form.valueRangeStart,
    form.valueRangeEnd,
    form.valueTransformOffset,
    form.expectedSequence,
    form.direction,
    form.observeChannelId,
    form.payloadActive,
    form.timeoutPolicy,
    form.dropAction,
    wizardState.organizationId,
    triggerUpsertDraft,
  ]);

  // Load EVERY modifier across all of the org's channels so the operator
  // sees the full saved set as soon as the step opens — not just the
  // currently-selected source channel. Modifiers are keyed by source
  // channel, so we fan out across `channels` and aggregate (dedup by id).
  // The source-channel select below is only for CREATING new modifiers; it
  // no longer gates the table. Previously the table stayed empty until a
  // source channel was picked, which made a completed (ACTIVE) org's saved
  // modifiers look missing — the draft that used to remember the selection
  // is deleted on "Complete Setup".
  const loadAllModifiers = useCallback(async () => {
    // Review's Channel Modifiers panel reads this flag. The step completes
    // itself on entry (it is optional), so completion cannot say whether any
    // modifier exists; every load — and every save and delete ends in one —
    // publishes the answer instead.
    const publishHasModifiers = (has: boolean) =>
      setWizardState((prev) =>
        prev.hasChannelModifiers === has ? prev : { ...prev, hasChannelModifiers: has },
      );
    if (channels.length === 0) {
      setModifiers([]);
      publishHasModifiers(false);
      return;
    }
    setIsLoading(true);
    try {
      const token = await GETJwtToken();
      // One channel at a time: a lazy trigger fired in parallel keeps only
      // its last call's result (see utils/settleInTurn).
      const settled = await settleInTurn(channels, async (ch) =>
        triggerGetModifiers({ channelId: ch.id, token }),
      );
      const results = settled.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
      // Responses arrive FlatBuffer-unpacked with camelCase property names;
      // `mapModifierRow` normalises camelCase and snake_case alike.
      const seen = new Set<string>();
      const all: ModifierRow[] = [];
      results.forEach((result) => {
        ((result.data?.data ?? result.data ?? []) as Record<string, unknown>[]).forEach((m) => {
          const row = mapModifierRow(m, channels);
          if (row.id && !seen.has(row.id)) {
            seen.add(row.id);
            all.push(row);
          }
        });
      });
      setModifiers(all);
      // Only a complete answer is published: a channel whose load failed may
      // hold modifiers, so "none found" is not "none exist".
      if (all.length > 0 || settled.every((r) => r.status === "fulfilled" && !r.value.isError)) {
        publishHasModifiers(all.length > 0);
      }
    } catch {
      setStepError("Failed to load modifiers");
    } finally {
      setIsLoading(false);
    }
  }, [channels, triggerGetModifiers]);

  useEffect(() => {
    loadAllModifiers();
  }, [loadAllModifiers]);

  // Save handler
  const handleSave = async () => {
    // Synchronous re-entry guard. See `savingRef` definition for the
    // double-click symptom this prevents — six identical rows from a
    // single user mash-clicking the button before RTK Query's
    // `isLoading` had a chance to engage the AntD `loading` lockout.
    if (savingRef.current) return;
    const err = validateForm(form, channelServerMap);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    // Clear stale error state so a previous failure's red banner
    // doesn't linger across a fresh attempt — otherwise GlobalAlert2 +
    // setStepError both fire at once, producing the "red box, no
    // description" ambiguity the operator reported.
    setStepError(null);
    resetPost();
    savingRef.current = true;

    // Wrap body construction in the try block too — `buildMessageT` can
    // throw on a malformed PayloadDraft, and we want that surfaced
    // through the same error path as a network failure rather than
    // bubbling silently to the React boundary.
    try {
      const payloadDraft = isSupportedTargetMessageType(form.targetMessageType)
        ? form.payload
        : null;

      // Schema v7 fields as saved: each only while its control is shown, so a
      // stale hidden value cannot make an ordinary modifier v7 (which an older
      // VTS skips). `schemaVersion` is 7 exactly when a v7 field is in use —
      // LiftCloud's RequiredSchemaVersion rule — and 6 otherwise.
      const v7 = effectiveV7Fields(form);

      // Keys MUST be camelCase here. The body goes through the wizard's
      // FlatBuffer base query → `CreateChannelModifier` request
      // serializer in `src/flatbuffers/api/requestSerializers.ts`, which
      // reads camelCase keys to build the `ChannelModifierT` struct
      // before packing it to binary. Snake-case keys silently produce a
      // T-struct with default/null fields, which then explodes at pack
      // time on the schema's required-field validation —
      // `Required field "source_channel_id" is not set`. The Go side
      // ultimately reads the binary FBT, not the JSON, so snake-case
      // JSON tags on `ChannelModifierRequest` don't gate this path.
      const body: Record<string, unknown> = {
        name: form.name,
        sourceChannelId: form.sourceChannelId,
        targetChannelId: form.targetChannelId,
        modifierType: form.modifierType,
        sourceMessageType:
          (form.sourceMessageRef?.messageContent as MessageContent | undefined) ??
          MessageContent.NONE,
        // Schema v4 source-leaf scalars — authoritative for the exact
        // leaf the modifier fires on. Sent alongside the union discriminant
        // (sourceMessageType above) for wire compatibility with older Go
        // readers that only check the union field.
        sourceMessageContent:
          (form.sourceMessageRef?.messageContent as MessageContent | undefined) ??
          MessageContent.NONE,
        sourceMessageValue: form.sourceMessageRef?.messageValue ?? 0,
        targetMessageType: form.targetMessageType,
        direction: v7.direction,
        priority: form.priority,
        active: form.active,
        schemaVersion: v7.schemaVersion,
        conditions: form.conditions.map((c) => ({
          fieldPath: c.fieldPath,
          op: c.op,
          valueText: c.valueText,
        })),
        actions: form.actions.map((a) => ({
          fieldPath: a.fieldPath,
          newValueText: a.newValueText,
        })),
        payload: buildMessageT(form.modifierType, payloadDraft),
        // Schema v3 value-transform directive — always sent so the
        // selected mode + parameters round-trip on every save. The
        // matching `t.valueTransformMode = intEnum(body, …)` reads in
        // the request serializer ensure they land on the FBT before
        // packing.
        valueTransformMode: form.valueTransformMode,
        valueRangeStart: form.valueRangeStart,
        valueRangeEnd: form.valueRangeEnd,
        valueTransformOffset: form.valueTransformOffset,
        // Steps without a picked message are dropped, and the whole list is
        // dropped while the sequence section is hidden (OUTBOUND AFTER,
        // IN_FLIGHT) — VTS ignores steps there, and a hidden step must not
        // make the modifier v7.
        expectedSequence: v7.steps,
        // Schema v6: conflict resolution policy — always sent so COEXIST
        // round-trips on pre-v6 rows and new rows alike.
        conflictPolicy: form.conflictPolicy,
        // Schema v7.
        payloadActive: v7.payloadActive,
        timeoutPolicy: v7.timeoutPolicy,
        dropAction: v7.dropAction,
        // "" = observe the target channel; the serializer sends no UUID.
        observeChannelId: v7.observeChannelId,
      };

      const token = await GETJwtToken();
      if (editingId) {
        await putModifier({ body: { ...body, id: editingId }, token }).unwrap();
      } else {
        await postModifier({ body, token }).unwrap();
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      resetPost();
      markStepCompleted(WizardStep.ChannelModifiers);
      await loadAllModifiers();
    } catch (rawErr) {
      // Don't swallow — surface the actual error string to the operator
      // via setStepError so the red banner above the form CARRIES the
      // detail (HTTP status, server message, parse error, etc.) instead
      // of just lighting up GlobalAlert2 with no text. Also dump to the
      // console so the full object is inspectable from devtools.
      const description = describeError(rawErr);
      const verb = editingId ? "update" : "create";
      setStepError(`Failed to ${verb} modifier — ${description}`);
      // eslint-disable-next-line no-console
      console.error(`[StepChannelModifiers] ${verb} failed`, rawErr);
    } finally {
      // Always clear the guard so the user can retry. If we left it set
      // on the error path the operator would be locked out of saving
      // until they re-mounted the step.
      savingRef.current = false;
    }
  };

  // Edit handler. Pre-populates the form with every field the row
  // carries so the operator can tweak a saved modifier without having
  // to re-pick channels or leaves they already chose. Source channel /
  // target channel come from the row's stored ids (preferred) with a
  // name-based fallback for older rows that predate the id-on-row
  // change.
  //
  // The target leaf — when the row's payload yielded one via
  // `extractLeafValueFromPayload` — is reconstructed into a full
  // FbMessageRef so the picker visibly carries the operator's original
  // choice instead of resetting to empty. The source leaf only has the
  // category discriminant available (the modifier doesn't store a
  // source-side payload), so we fall back to a "category-only" ref
  // when the picker has no choice to display.
  const handleEdit = (row: ModifierRow) => {
    const src =
      row.sourceChannelId || channels.find((c) => c.name === row.sourceChannelName)?.id || "";
    const tgt =
      row.targetChannelId || channels.find((c) => c.name === row.targetChannelName)?.id || "";

    // Build the target picker ref from the leaf we cached at load time.
    // Without a leaf (older rows / NONE / unsupported category) we set
    // the ref to null so the picker stays empty and validation forces a
    // re-pick before save.
    const targetRef: FbMessageRef | null =
      row.targetMessageValue !== null && row.targetMessageType !== MessageContent.NONE
        ? {
            // OneHot vs BitMask isn't stored on the row; the picker hard-codes
            // encodedType=2 (BitMask) for every category, so mirror that.
            encodedType: 2,
            messageContent: row.targetMessageType,
            messageValue: row.targetMessageValue,
            deviceType: 0,
            label: `${MessageContent[row.targetMessageType] ?? "Unknown"}:${
              row.targetMessageValue
            }`,
          }
        : null;

    // Restore the exact source leaf from the v4 scalar pair. Rows that
    // pre-date v4 will have sourceMessageValue=0, which is still better
    // than the previous hard-coded zero placeholder because it at least
    // carries the real category discriminant and a meaningful label.
    const sourceRef: FbMessageRef | null =
      row.sourceMessageType !== MessageContent.NONE
        ? {
            encodedType: 2,
            messageContent: row.sourceMessageType,
            messageValue: row.sourceMessageValue,
            deviceType: 0,
            label: `${MessageContent[row.sourceMessageType] ?? "Unknown"}:${
              row.sourceMessageValue
            }`,
          }
        : null;

    function buildSeqStepRef(s: StoredSequenceStep): SequenceStepRow {
      const content = s.expectedMessageContent;
      const value = s.expectedMessageValue;
      const messageRef: FbMessageRef | null =
        content !== MessageContent.NONE
          ? {
              encodedType: 2,
              messageContent: content,
              messageValue: value,
              deviceType: 0,
              label: `${MessageContent[content] ?? "Unknown"}:${value}`,
            }
          : null;
      return {
        messageRef,
        timeoutMs: s.timeoutMs,
        expectedActive: s.expectedActive,
        scope: s.scope,
      };
    }

    const stepRefs: SequenceStepRow[] = (row.expectedSequence ?? []).map(buildSeqStepRef);

    const targetPayload =
      row.targetMessageValue !== null && row.targetMessageType !== MessageContent.NONE
        ? payloadDraftFromLeaf(row.targetMessageType, row.targetMessageValue)
        : defaultPayloadDraft(MessageContent.Status_StatusMessage);

    setForm({
      name: row.name,
      sourceChannelId: src,
      targetChannelId: tgt,
      modifierType: row.modifierType,
      sourceMessageRef: sourceRef,
      targetMessageType:
        row.targetMessageType !== MessageContent.NONE
          ? row.targetMessageType
          : MessageContent.Status_StatusMessage,
      targetMessageRef: targetRef,
      priority: row.priority,
      conditions: row.conditions ?? [],
      actions: row.actions ?? [],
      payload: targetPayload,
      active: row.active,
      valueTransformMode: row.valueTransformMode,
      valueRangeStart: row.valueRangeStart,
      valueRangeEnd: row.valueRangeEnd,
      valueTransformOffset: row.valueTransformOffset,
      expectedSequence: stepRefs,
      conflictPolicy: row.conflictPolicy ?? ConflictPolicy.COEXIST,
      direction: row.direction,
      observeChannelId: row.observeChannelId,
      payloadActive: row.payloadActive,
      timeoutPolicy: row.timeoutPolicy,
      dropAction: row.dropAction,
    });
    setEditingId(row.id);
    setValidationError(null);
    resetPost();
  };

  // Delete handler
  const handleDelete = async (id: string) => {
    try {
      const token = await GETJwtToken();
      await deleteModifier({ id, token }).unwrap();
      await loadAllModifiers();
    } catch {
      setStepError("Failed to delete modifier");
    }
  };

  // Bulk-delete handler for the "Delete All" button. Fires one DELETE
  // per modifier in the current source-channel's list and refreshes
  // afterwards. The button's Popconfirm guards against accidental
  // clicks — there's no undo. `isDeletingAll` drives the spinner and
  // disables the button while requests are in flight so the operator
  // can't kick off a second bulk-delete during the first one.
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const handleDeleteAll = async () => {
    if (modifiers.length === 0 || isDeletingAll) return;
    setIsDeletingAll(true);
    try {
      const token = await GETJwtToken();
      await Promise.all(modifiers.map((row) => deleteModifier({ id: row.id, token }).unwrap()));
      await loadAllModifiers();
    } catch {
      setStepError("Failed to delete one or more modifiers");
    } finally {
      setIsDeletingAll(false);
    }
  };

  const isSaving = isPosting || isPutting;

  // Table columns
  const columns: ColumnsType<ModifierRow> = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Source Channel", dataIndex: "sourceChannelName", key: "sourceChannelName" },
    { title: "Target Channel", dataIndex: "targetChannelName", key: "targetChannelName" },
    {
      title: "Type",
      dataIndex: "modifierType",
      key: "modifierType",
      render: (v: ModifierType) => MODIFIER_TYPE_LABEL[v] ?? String(v),
    },
    { title: "Priority", dataIndex: "priority", key: "priority" },
    {
      title: "Value Mode",
      key: "valueMode",
      render: (_: unknown, row: ModifierRow) => valueModeSummary(row),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (v: boolean) => (v ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, row: ModifierRow) => (
        <>
          <WizardOpGate op="update">
            <Button size="small" onClick={() => handleEdit(row)} style={{ marginRight: 8 }}>
              Edit
            </Button>
          </WizardOpGate>
          <WizardOpGate op="delete">
            <Popconfirm
              title="Delete this modifier?"
              onConfirm={() => handleDelete(row.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          </WizardOpGate>
        </>
      ),
    },
  ];

  const sourceChannelOptions = useMemo(
    () =>
      channels
        .filter((c) => c.id !== form.targetChannelId)
        .map((c) => ({ label: c.name, value: c.id })),
    [channels, form.targetChannelId],
  );

  // Build a leaf-message filter for a given channel by unioning every
  // source we know how to extract leaf-level mappings from:
  //   1) the adapter-keys-table (LN1-style adapters),
  //   2) the adapter's Modbus CDO template (Modbus / register-offset
  //      adapters — leaf mappings live in the binary `cdo_template`
  //      rather than the keys table), and
  //   3) any `bank.offsetMappingJson` keys we could parse.
  //
  // The result is the union of `"<messageContent>:<messageValue>"` keys
  // the operator has configured on the adapter side. Returns `undefined`
  // when no channel is picked yet so the picker falls back to showing
  // every option.
  const buildChannelMessageFilter = useCallback(
    (channelId: string): Set<string> | undefined => {
      if (!channelId) return undefined;
      const out = new Set<string>();
      (channelAdapters[channelId] ?? []).forEach((adapterId) => {
        const adapterKeys = adapterMessageKeys[adapterId];
        if (adapterKeys) adapterKeys.forEach((key) => out.add(key));
        const cdoKeys = adapterCdoMessageKeys[adapterId];
        if (cdoKeys) cdoKeys.forEach((key) => out.add(key));
      });
      const bankKeys = bankMessageKeys[channelId];
      if (bankKeys) bankKeys.forEach((key) => out.add(key));
      return out;
    },
    [channelAdapters, adapterMessageKeys, adapterCdoMessageKeys, bankMessageKeys],
  );

  const sourceMessageValueFilter = useMemo(
    () => buildChannelMessageFilter(form.sourceChannelId),
    [buildChannelMessageFilter, form.sourceChannelId],
  );

  const targetMessageValueFilter = useMemo(
    () => buildChannelMessageFilter(form.targetChannelId),
    [buildChannelMessageFilter, form.targetChannelId],
  );

  // Expected steps are observed on the observe channel, which is the target
  // channel unless the modifier names another — so the step picker offers what
  // THAT channel's adapter maps. With no observe channel it is the very same
  // filter object the target picker gets.
  const sequenceMessageValueFilter = useMemo(
    () =>
      form.observeChannelId
        ? buildChannelMessageFilter(form.observeChannelId)
        : targetMessageValueFilter,
    [buildChannelMessageFilter, form.observeChannelId, targetMessageValueFilter],
  );

  const observeOptions = useMemo(
    () => observeChannelOptions(channels, form.sourceChannelId, form.modifierType),
    [channels, form.sourceChannelId, form.modifierType],
  );

  // An observe channel that the source or type no longer allows (another
  // server, or the source itself under BEFORE) falls back to the target.
  useEffect(() => {
    if (!form.observeChannelId || !form.sourceChannelId || channels.length === 0) return;
    if (!observeOptions.some((o) => o.value === form.observeChannelId)) {
      setForm((prev) => ({ ...prev, observeChannelId: "" }));
    }
  }, [observeOptions, form.observeChannelId, form.sourceChannelId, channels.length]);

  // Every mapping of a leaf on the observe channel, across all of its adapters.
  const observeLeafMappings = useCallback(
    (key: string): LeafMapping[] => {
      const channelId = form.observeChannelId || form.targetChannelId;
      if (!channelId) return [];
      return (channelAdapters[channelId] ?? []).flatMap((adapterId) => [
        ...(adapterCdoLeafMappings[adapterId]?.get(key) ?? []),
        ...(adapterKeyLeafMappings[adapterId]?.get(key) ?? []),
      ]);
    },
    [
      form.observeChannelId,
      form.targetChannelId,
      channelAdapters,
      adapterCdoLeafMappings,
      adapterKeyLeafMappings,
    ],
  );

  const isInbound = form.direction === ModifierDirection.INBOUND;
  const sequenceVisible = isSequenceVisible(form.modifierType, form.direction);
  const timeoutPolicyVisible = isTimeoutPolicyVisible(
    form.modifierType,
    form.direction,
    form.expectedSequence.length,
  );

  const targetChannelOptions = useMemo(
    () =>
      channels
        .filter((c) => c.id !== form.sourceChannelId)
        .map((c) => ({ label: c.name, value: c.id })),
    [channels, form.sourceChannelId],
  );

  // If source changes to the value currently in target, clear target to keep them distinct.
  useEffect(() => {
    if (
      form.sourceChannelId &&
      form.targetChannelId &&
      form.sourceChannelId === form.targetChannelId
    ) {
      setForm((prev) => ({ ...prev, targetChannelId: "" }));
    }
  }, [form.sourceChannelId, form.targetChannelId]);

  function moveStep(steps: SequenceStepRow[], idx: number, dir: "up" | "down"): SequenceStepRow[] {
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= steps.length) return steps;
    const updated = [...steps];
    const temp = updated[idx]!;
    updated[idx] = updated[swapIdx]!;
    updated[swapIdx] = temp;
    return updated;
  }

  return (
    <div data-testid="step-channel-modifiers">
      <GlobalAlert2
        isError={isErrorPost}
        isSuccess={isSuccessPost}
        requestType="POST"
        error={errorPost}
        name="Channel Modifier"
      />

      <DrawerRowDivider icon={faCodeBranch} title={t("Channel Modifiers")} />

      <WizardExportButton stage={5} orgId={wizardState.organizationId} />

      {stepError && (
        <Alert
          message={stepError}
          type="error"
          closable
          onClose={() => setStepError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Form */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <span className="generalStyles__inputLabel">{t("Name")}</span>
          <Input
            data-testid="modifier-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Modifier name"
            style={{ width: "100%", marginTop: 4 }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <span className="generalStyles__inputLabel">{t("Source Channel")}</span>
          <Select
            data-testid="modifier-source-channel"
            style={{ width: "100%", marginTop: 4 }}
            options={sourceChannelOptions}
            value={form.sourceChannelId || undefined}
            onChange={(v) =>
              setForm({
                ...form,
                sourceChannelId: v ?? "",
                // Clear the source leaf when the channel changes so the
                // operator re-picks a message that's actually meaningful
                // for the new channel instead of carrying over a stale
                // pick from a different bank.
                sourceMessageRef: null,
              })
            }
            placeholder="Select source channel"
            allowClear
          />
        </div>

        <div style={{ marginBottom: 8 }} data-testid="modifier-source-message">
          <span className="generalStyles__inputLabel">{t("Source Message")}</span>
          <div style={{ marginTop: 4 }}>
            <FlatbufferMessagePicker
              value={form.sourceMessageRef}
              onChange={(ref) => setForm({ ...form, sourceMessageRef: ref })}
              disabled={!form.sourceChannelId}
              placeholder={
                form.sourceChannelId
                  ? "Select source message (type to search, e.g. Control::EnergizeCarCall)"
                  : "Select a source channel first"
              }
              // Restrict to the leaf messages the source channel's
              // adapter actually has mapped (via the keys table or the
              // bank's offset matrix).
              messageValueFilter={sourceMessageValueFilter}
              allowClear
            />
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <span className="generalStyles__inputLabel">{t("Target Channel")}</span>
          <Select
            data-testid="modifier-target-channel"
            style={{ width: "100%", marginTop: 4 }}
            options={targetChannelOptions}
            value={form.targetChannelId || undefined}
            onChange={(v) =>
              setForm({
                ...form,
                targetChannelId: v ?? "",
                // Mirror the source-side behaviour: clearing the leaf on
                // channel change keeps picker and channel in lockstep.
                targetMessageRef: null,
              })
            }
            placeholder="Select target channel"
            allowClear
          />
        </div>

        {/* Direction (schema v7). Outbound fires on commands going to the
           source vendor; Inbound fires on state changes the source vendor
           reports. Inbound has nothing to hold back, so it forces After and
           rules out Before, InFlight, QUEUE, DROP and REVERT. */}
        <div style={{ marginBottom: 8 }}>
          <span className="generalStyles__inputLabel">{t("Direction")}</span>
          <div style={{ marginTop: 4 }}>
            <Radio.Group
              data-testid="modifier-direction-radio"
              value={form.direction}
              onChange={(e) => setForm(withDirection(form, e.target.value as ModifierDirection))}
            >
              <Radio data-testid="modifier-direction-outbound" value={ModifierDirection.OUTBOUND}>
                Outbound
              </Radio>
              <Radio data-testid="modifier-direction-inbound" value={ModifierDirection.INBOUND}>
                Inbound
              </Radio>
            </Radio.Group>
          </div>
          <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
            {t(
              "Outbound: fires on commands sent to the source channel. " +
                "Inbound: fires when the source channel reports a state change (After only).",
            )}
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <span className="generalStyles__inputLabel">{t("Modifier Type")}</span>
          <div style={{ marginTop: 4 }}>
            <Radio.Group
              data-testid="modifier-type-radio"
              value={form.modifierType}
              onChange={(e) => setForm({ ...form, modifierType: e.target.value as ModifierType })}
            >
              <Radio value={ModifierType.BEFORE} disabled={isInbound}>
                Before
              </Radio>
              <Radio value={ModifierType.AFTER}>After</Radio>
              <Radio value={ModifierType.IN_FLIGHT} disabled={isInbound}>
                InFlight
              </Radio>
            </Radio.Group>
          </div>
        </div>

        <div style={{ marginBottom: 8 }} data-testid="modifier-message-type">
          <span className="generalStyles__inputLabel">{t("Target Message")}</span>
          <div style={{ marginTop: 4 }}>
            <FlatbufferMessagePicker
              value={form.targetMessageRef}
              onChange={(ref) => {
                if (ref === null) {
                  setForm({ ...form, targetMessageRef: null });
                  return;
                }
                const mc = ref.messageContent as MessageContent;
                setForm({
                  ...form,
                  targetMessageRef: ref,
                  targetMessageType: mc,
                  payload: payloadDraftFromLeaf(mc, ref.messageValue),
                });
              }}
              disabled={!form.targetChannelId}
              placeholder={
                form.targetChannelId
                  ? "Select target message (type to search, e.g. Traffic::CarCall)"
                  : "Select a target channel first"
              }
              // Same per-channel adapter filter as the source picker.
              messageValueFilter={targetMessageValueFilter}
              allowClear
            />
          </div>
        </div>

        {/* Payload state (schema v7): the `active` flag stamped on the
           payload. Vendor command maps key on the whole valued message, so this
           is what makes one target message select opposite commands — a
           relay's on and off. "Active" stores UNSET, the pre-v7 active:true,
           so choosing it never makes a modifier v7. */}
        {isPayloadActiveVisible(form.modifierType) && (
          <div style={{ marginBottom: 8 }}>
            <span className="generalStyles__inputLabel">{t("Payload State")}</span>
            <Select
              data-testid="modifier-payload-active-select"
              style={{ width: "100%", marginTop: 4 }}
              value={
                form.payloadActive === ActiveState.INACTIVE
                  ? ActiveState.INACTIVE
                  : ActiveState.UNSET
              }
              onChange={(v: ActiveState) => setForm({ ...form, payloadActive: v })}
              options={[
                { label: "Active (default)", value: ActiveState.UNSET },
                { label: "Inactive", value: ActiveState.INACTIVE },
              ]}
            />
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <span className="generalStyles__inputLabel">{t("Priority")}</span>
          <InputNumber
            data-testid="modifier-priority"
            style={{ width: "100%", marginTop: 4 }}
            value={form.priority}
            onChange={(v) => setForm({ ...form, priority: v ?? 0 })}
            min={0}
          />
        </div>

        {/* Value Transform Mode — controls how the source-message
           payload value flows to the target message. PASSTHROUGH (the
           default) preserves the legacy behavior. SPECIFIC scopes the
           modifier to a value range. TRANSFORMATIVE applies a signed
           offset. Conditional inputs render only when their mode is
           selected so PASSTHROUGH stays a clean one-line choice. */}
        <div style={{ marginBottom: 8 }} data-testid="modifier-value-mode-section">
          <span className="generalStyles__inputLabel">{t("Value Mode")}</span>
          <div style={{ marginTop: 4 }}>
            <Radio.Group
              data-testid="modifier-value-mode-radio"
              value={form.valueTransformMode}
              onChange={(e) =>
                setForm({ ...form, valueTransformMode: e.target.value as ValueTransformMode })
              }
            >
              <Radio value={ValueTransformMode.PASSTHROUGH}>Passthrough</Radio>
              <Radio value={ValueTransformMode.SPECIFIC}>Specific</Radio>
              <Radio value={ValueTransformMode.TRANSFORMATIVE}>Transformative</Radio>
            </Radio.Group>
          </div>
          <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
            {t(
              "Passthrough: target gets source value unchanged. " +
                "Specific: only fires for values in [start, end). " +
                "Transformative: target value = source value + offset.",
            )}
          </div>
        </div>

        {form.valueTransformMode === ValueTransformMode.SPECIFIC && (
          <div
            style={{ marginBottom: 8, display: "flex", gap: 8 }}
            data-testid="modifier-value-mode-specific"
          >
            <div style={{ flex: 1 }}>
              <span className="generalStyles__inputLabel">{t("Range Start (inclusive)")}</span>
              <InputNumber
                data-testid="modifier-value-range-start"
                style={{ width: "100%", marginTop: 4 }}
                value={form.valueRangeStart}
                onChange={(v) => setForm({ ...form, valueRangeStart: v ?? 0 })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <span className="generalStyles__inputLabel">{t("Range End (exclusive)")}</span>
              <InputNumber
                data-testid="modifier-value-range-end"
                style={{ width: "100%", marginTop: 4 }}
                value={form.valueRangeEnd}
                onChange={(v) => setForm({ ...form, valueRangeEnd: v ?? 0 })}
              />
            </div>
          </div>
        )}

        {form.valueTransformMode === ValueTransformMode.TRANSFORMATIVE && (
          <div style={{ marginBottom: 8 }} data-testid="modifier-value-mode-transformative">
            <span className="generalStyles__inputLabel">{t("Offset (signed)")}</span>
            <InputNumber
              data-testid="modifier-value-transform-offset"
              style={{ width: "100%", marginTop: 4 }}
              value={form.valueTransformOffset}
              onChange={(v) => setForm({ ...form, valueTransformOffset: v ?? 0 })}
            />
            <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
              {t(
                "Negative values are allowed — e.g. -1 maps source position 3 to target position 2.",
              )}
            </div>
          </div>
        )}

        {/* Conflict Policy — visible for BEFORE/AFTER modifiers only.
           IN_FLIGHT mutates the source in place and never emits a target
           payload, so it cannot participate in a target-channel conflict
           group. The governing modifier (lowest priority value) in a
           conflict group applies its policy to all lower-precedence peers
           that emit to the same target channel + target message. */}
        {(form.modifierType === ModifierType.BEFORE ||
          form.modifierType === ModifierType.AFTER) && (
          <div style={{ marginBottom: 8 }} data-testid="modifier-conflict-policy-section">
            <span className="generalStyles__inputLabel">{t("Conflict Policy")}</span>
            <div style={{ marginTop: 4 }}>
              <Select
                data-testid="modifier-conflict-policy-select"
                style={{ width: "100%" }}
                value={form.conflictPolicy}
                onChange={(v: ConflictPolicy) => setForm({ ...form, conflictPolicy: v })}
                // QUEUE holds emissions for a later drain; an Inbound modifier
                // reacts to a state change already reported, so it is ruled out.
                options={Object.entries(ConflictPolicy)
                  .filter(([, v]) => typeof v === "number")
                  .map(([label, value]) => ({
                    label,
                    value: value as ConflictPolicy,
                    disabled: isInbound && value === ConflictPolicy.QUEUE,
                  }))}
              />
            </div>
            <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
              {t("Governs how this modifier resolves with others emitting to the same target.")}
            </div>
          </div>
        )}

        {/* Payload editor:
           Hidden for IN_FLIGHT (no emission).
           Hidden until the operator picks a target leaf message — the
           picker fully specifies the leaf, the device kind, and the
           variant, so we only surface this section when there's residual
           payload-side data the picker can't express (Traffic position,
           ControllerInfo content). Status / Usage / Service / Control /
           Fault / GroupControl / GroupSupervisoryStatus collapse to a
           single leaf pick — no Payload section at all. */}
        {(form.modifierType === ModifierType.BEFORE || form.modifierType === ModifierType.AFTER) &&
          form.targetMessageRef !== null &&
          targetMessageHasOperatorInputs(form.targetMessageType) && (
            <div style={{ marginBottom: 8 }} data-testid="modifier-payload-editor">
              <span className="generalStyles__inputLabel">{t("Payload")}</span>
              {isSupportedTargetMessageType(form.targetMessageType) ? (
                <PayloadEditor
                  draft={form.payload}
                  onChange={(p) => setForm({ ...form, payload: p })}
                />
              ) : (
                <Alert
                  data-testid="modifier-payload-unsupported"
                  type="info"
                  showIcon
                  message={t(
                    "Payload editor for this message type is not yet implemented in the wizard.",
                  )}
                  style={{ marginTop: 4 }}
                />
              )}
            </div>
          )}

        {/* Conditions sub-form: available for every modifier type. VTS ANDs these
            against the original, unmutated message before deciding whether the
            rule fires at all, so a BEFORE rule can be scoped to a single floor
            with `/msg/msg/content/one_hot_message/traffic_traffic_message/position`.
            Gating this on IN_FLIGHT made a floor-scoped BEFORE/AFTER modifier
            unauthorable even though the backend and the CDO both carry it.
            `actions` below stay IN_FLIGHT-only: they rewrite the source message
            in place, which means something for that type alone. */}
        <div style={{ marginBottom: 8 }} data-testid="modifier-conditions-section">
          <span className="generalStyles__inputLabel">{t("Conditions")}</span>
          <div style={{ marginTop: 4, padding: 8, border: "1px solid #ddd" }}>
            {form.conditions.length === 0 && (
              <span style={{ color: "#999" }}>No conditions — add below</span>
            )}
            {form.conditions.map((cond, idx) => (
              <div key={cond.key} style={{ marginBottom: 4, display: "flex", gap: 8 }}>
                <Input
                  placeholder="Field path"
                  value={cond.fieldPath}
                  onChange={(e) => {
                    const updated = [...form.conditions];
                    updated[idx] = { ...updated[idx]!, fieldPath: e.target.value };
                    setForm({ ...form, conditions: updated });
                  }}
                  style={{ flex: 2 }}
                />
                <Select
                  style={{ flex: 1 }}
                  value={cond.op}
                  options={Object.entries(ConditionOp)
                    .filter(([, v]) => typeof v === "number")
                    .map(([label, value]) => ({ label, value: value as ConditionOp }))}
                  onChange={(v) => {
                    const updated = [...form.conditions];
                    updated[idx] = { ...updated[idx]!, op: v };
                    setForm({ ...form, conditions: updated });
                  }}
                />
                <Input
                  placeholder="Value"
                  value={cond.valueText}
                  onChange={(e) => {
                    const updated = [...form.conditions];
                    updated[idx] = { ...updated[idx]!, valueText: e.target.value };
                    setForm({ ...form, conditions: updated });
                  }}
                  style={{ flex: 2 }}
                />
                <Button
                  size="small"
                  danger
                  onClick={() =>
                    setForm({
                      ...form,
                      conditions: form.conditions.filter((_, i) => i !== idx),
                    })
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              size="small"
              onClick={() =>
                setForm({
                  ...form,
                  conditions: [
                    ...form.conditions,
                    {
                      key: String(Date.now()),
                      fieldPath: "",
                      op: ConditionOp.EQ,
                      valueText: "",
                    },
                  ],
                })
              }
            >
              Add Condition
            </Button>
          </div>
        </div>

        {/* Actions sub-form: visible for IN_FLIGHT only */}
        {form.modifierType === ModifierType.IN_FLIGHT && (
          <div style={{ marginBottom: 8 }} data-testid="modifier-actions-section">
            <span className="generalStyles__inputLabel">{t("Actions")}</span>
            <div style={{ marginTop: 4, padding: 8, border: "1px solid #ddd" }}>
              {form.actions.length === 0 && (
                <span style={{ color: "#999" }}>No actions — add below</span>
              )}
              {form.actions.map((action, idx) => (
                <div key={action.key} style={{ marginBottom: 4, display: "flex", gap: 8 }}>
                  <Input
                    placeholder="Field path"
                    value={action.fieldPath}
                    onChange={(e) => {
                      const updated = [...form.actions];
                      updated[idx] = { ...updated[idx]!, fieldPath: e.target.value };
                      setForm({ ...form, actions: updated });
                    }}
                    style={{ flex: 2 }}
                  />
                  <Input
                    placeholder="New value"
                    value={action.newValueText}
                    onChange={(e) => {
                      const updated = [...form.actions];
                      updated[idx] = { ...updated[idx]!, newValueText: e.target.value };
                      setForm({ ...form, actions: updated });
                    }}
                    style={{ flex: 2 }}
                  />
                  <Button
                    size="small"
                    danger
                    onClick={() =>
                      setForm({
                        ...form,
                        actions: form.actions.filter((_, i) => i !== idx),
                      })
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                size="small"
                onClick={() =>
                  setForm({
                    ...form,
                    actions: [
                      ...form.actions,
                      { key: String(Date.now()), fieldPath: "", newValueText: "" },
                    ],
                  })
                }
              >
                Add Action
              </Button>
            </div>
          </div>
        )}

        {/* Expected Message Sequence: visible for BEFORE modifiers, where it
           gates the held source message, and for INBOUND AFTER modifiers,
           where it is observed and logged only. */}
        {sequenceVisible && (
          <div style={{ marginBottom: 8 }} data-testid="modifier-sequence-section">
            <span className="generalStyles__inputLabel">{t("Expected Message Sequence")}</span>
            <div style={{ marginTop: 4, color: "#888", fontSize: 12, marginBottom: 8 }}>
              {isInbound
                ? t(
                    "Messages VTS watches for on the observe channel after sending the payload, in order",
                  )
                : t(
                    "Messages VTS must receive on the observe channel before releasing the source message",
                  )}
            </div>
            {isInbound && (
              <Alert
                data-testid="modifier-sequence-inbound-note"
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
                message={t("Observed and logged only; never holds the message")}
              />
            )}

            {/* Observe channel (schema v7): where the steps are watched for.
               Same server as the source; never the source itself for Before,
               whose loop is parked while the gate waits. */}
            <div style={{ marginBottom: 8 }}>
              <span className="generalStyles__inputLabel">{t("Observe Channel")}</span>
              <Select
                data-testid="modifier-observe-channel-select"
                style={{ width: "100%", marginTop: 4 }}
                value={form.observeChannelId}
                options={observeOptions}
                onChange={(v: string) => setForm({ ...form, observeChannelId: v ?? "" })}
              />
              <div
                data-testid="modifier-sequence-scope-help"
                style={{ color: "#888", fontSize: 12, marginTop: 4 }}
              >
                {t(
                  "A step is satisfied only by a change the observe channel reports after the payload write starts. " +
                    "Scope: Same floor compares floors only when the confirmation message carries one — a message without a position (Status, Usage) matches any floor. " +
                    "Same floor + car also requires the source message's car.",
                )}
              </div>
            </div>

            {form.expectedSequence.map((step, idx) => {
              const collision = stepLeafCollision(
                step.messageRef,
                form.sourceMessageRef,
                form.targetMessageRef,
              );
              const mappings = step.messageRef
                ? observeLeafMappings(
                    leafKey(step.messageRef.messageContent, step.messageRef.messageValue),
                  )
                : [];
              const ambiguous = mappingsAmbiguous(mappings, step.scope);
              return (
                <div key={idx} style={{ marginBottom: 8 }}>
                  <div
                    data-testid={`modifier-sequence-step-${idx}`}
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <div style={{ flex: 2 }} data-testid={`modifier-sequence-message-${idx}`}>
                      <FlatbufferMessagePicker
                        value={step.messageRef}
                        onChange={(ref) => {
                          const updated = [...form.expectedSequence];
                          updated[idx] = { ...updated[idx]!, messageRef: ref };
                          setForm({ ...form, expectedSequence: updated });
                        }}
                        placeholder="Select expected message"
                        // The OBSERVE channel's filter: a step is watched for
                        // there, so only messages its adapter maps are
                        // offerable (the target's, when no observe channel).
                        messageValueFilter={sequenceMessageValueFilter}
                        allowClear
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span className="generalStyles__inputLabel">{t("Expected State")}</span>
                      <Select
                        data-testid={`modifier-sequence-expected-active-${idx}`}
                        style={{ width: "100%" }}
                        value={step.expectedActive}
                        onChange={(v: ActiveState) => {
                          const updated = [...form.expectedSequence];
                          updated[idx] = { ...updated[idx]!, expectedActive: v };
                          setForm({ ...form, expectedSequence: updated });
                        }}
                        options={[
                          { label: "Any", value: ActiveState.UNSET },
                          { label: "Active", value: ActiveState.ACTIVE },
                          { label: "Inactive", value: ActiveState.INACTIVE },
                        ]}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span className="generalStyles__inputLabel">{t("Scope")}</span>
                      <Select
                        data-testid={`modifier-sequence-scope-${idx}`}
                        style={{ width: "100%" }}
                        value={step.scope}
                        onChange={(v: SequenceStepScope) => {
                          const updated = [...form.expectedSequence];
                          updated[idx] = { ...updated[idx]!, scope: v };
                          setForm({ ...form, expectedSequence: updated });
                        }}
                        options={[
                          { label: "Same floor", value: SequenceStepScope.SAME_POSITION },
                          { label: "Any floor", value: SequenceStepScope.ANY_POSITION },
                          {
                            label: "Same floor + car",
                            value: SequenceStepScope.SAME_POSITION_AND_LIFT,
                          },
                        ]}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span className="generalStyles__inputLabel">{t("Timeout (ms)")}</span>
                      <InputNumber
                        data-testid={`modifier-sequence-timeout-${idx}`}
                        min={0}
                        value={step.timeoutMs}
                        onChange={(v) => {
                          const updated = [...form.expectedSequence];
                          updated[idx] = { ...updated[idx]!, timeoutMs: v ?? 2000 };
                          setForm({ ...form, expectedSequence: updated });
                        }}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <Button
                      size="small"
                      data-testid={`modifier-sequence-up-${idx}`}
                      disabled={idx === 0}
                      onClick={() =>
                        setForm({
                          ...form,
                          expectedSequence: moveStep(form.expectedSequence, idx, "up"),
                        })
                      }
                    >
                      ↑
                    </Button>
                    <Button
                      size="small"
                      data-testid={`modifier-sequence-down-${idx}`}
                      disabled={idx === form.expectedSequence.length - 1}
                      onClick={() =>
                        setForm({
                          ...form,
                          expectedSequence: moveStep(form.expectedSequence, idx, "down"),
                        })
                      }
                    >
                      ↓
                    </Button>
                    <Button
                      size="small"
                      danger
                      data-testid={`modifier-sequence-remove-${idx}`}
                      onClick={() => {
                        const updated = form.expectedSequence.filter((_, i) => i !== idx);
                        setForm({ ...form, expectedSequence: updated });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                  {collision !== null && (
                    <Alert
                      data-testid={`modifier-sequence-step-reuse-${idx}`}
                      type="info"
                      showIcon
                      style={{ marginTop: 4 }}
                      message={t(stepCollisionText(collision))}
                    />
                  )}
                  {ambiguous && (
                    <Alert
                      data-testid={`modifier-sequence-step-warning-${idx}`}
                      type="warning"
                      showIcon
                      style={{ marginTop: 4 }}
                      message={t(
                        `The observe channel maps this message ${mappings.length} times, and this step's scope cannot tell them apart: ` +
                          "a change on any of them satisfies the step. If several locks share this confirmation, give each its own message or position.",
                      )}
                    />
                  )}
                </div>
              );
            })}
            <Button
              size="small"
              data-testid="modifier-sequence-add-step"
              onClick={() =>
                setForm({
                  ...form,
                  expectedSequence: [
                    ...form.expectedSequence,
                    {
                      messageRef: null,
                      timeoutMs: 2000,
                      expectedActive: ActiveState.UNSET,
                      // On a multi-car bank a floor alone does not say which
                      // car's device answered, so new steps also pin the car.
                      scope: defaultStepScope(channelMaxLifts[form.sourceChannelId] ?? 0),
                    },
                  ],
                })
              }
            >
              {t("Add Step")}
            </Button>

            {/* On confirmation timeout (D1): Before with steps only. RELEASE
               fails open (the pre-v7 behaviour); DROP fails closed and the
               operator sees the command as Withheld. */}
            {timeoutPolicyVisible && (
              <div style={{ marginTop: 8 }}>
                <span className="generalStyles__inputLabel">{t("On Confirmation Timeout")}</span>
                <Select
                  data-testid="modifier-timeout-policy-select"
                  style={{ width: "100%", marginTop: 4 }}
                  value={form.timeoutPolicy}
                  onChange={(v: ModifierTimeoutPolicy) =>
                    setForm({
                      ...form,
                      timeoutPolicy: v,
                      dropAction:
                        v === ModifierTimeoutPolicy.DROP
                          ? form.dropAction
                          : ModifierDropAction.NONE,
                    })
                  }
                  options={[
                    { label: "Release car call", value: ModifierTimeoutPolicy.RELEASE },
                    {
                      label: "Drop car call",
                      value: ModifierTimeoutPolicy.DROP,
                      disabled: isInbound,
                    },
                  ]}
                />
              </div>
            )}

            {/* After a drop (D2): only when DROP. REVERT re-sends the payload
               with `active` inverted, so a failed unlock does not leave the
               lock open. */}
            {timeoutPolicyVisible && form.timeoutPolicy === ModifierTimeoutPolicy.DROP && (
              <div style={{ marginTop: 8 }}>
                <span className="generalStyles__inputLabel">{t("After a Drop")}</span>
                <Select
                  data-testid="modifier-drop-action-select"
                  style={{ width: "100%", marginTop: 4 }}
                  value={form.dropAction}
                  onChange={(v: ModifierDropAction) => setForm({ ...form, dropAction: v })}
                  options={[
                    { label: "Leave target as is", value: ModifierDropAction.NONE },
                    {
                      label: "Revert target",
                      value: ModifierDropAction.REVERT,
                      disabled: isInbound,
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}

        {/* Validation error */}
        {validationError && (
          <Alert
            data-testid="modifier-validation-error"
            message={validationError}
            type="error"
            style={{ marginBottom: 8 }}
          />
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button
            data-testid="modifier-save-btn"
            type="primary"
            loading={isSaving}
            disabled={editingId ? !access.update : !access.create}
            onClick={() => handleSave()}
          >
            {editingId ? "Update Modifier" : "Add Modifier"}
          </Button>
          {editingId && (
            <Button
              onClick={() => {
                setForm(EMPTY_FORM);
                setEditingId(null);
                setValidationError(null);
                resetPost();
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      <Divider />

      {/* Modifier list — header row exposes a bulk "Delete All" so the
         operator can clean up leftover duplicates from the pre-fix
         retry-loop bug in one click instead of seven. The button only
         renders when the list has rows; the Popconfirm guards the
         destructive action. */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <span className="generalStyles__inputLabel">
          {t("Modifiers")} ({modifiers.length})
        </span>
        {modifiers.length > 0 && (
          <WizardOpGate op="delete">
            <Popconfirm
              title={`Delete all ${modifiers.length} modifier${
                modifiers.length === 1 ? "" : "s"
              } for this source channel? This cannot be undone.`}
              onConfirm={() => handleDeleteAll()}
              okText="Delete All"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                data-testid="modifier-delete-all-btn"
                size="small"
                danger
                loading={isDeletingAll}
                disabled={isDeletingAll}
              >
                Delete All
              </Button>
            </Popconfirm>
          </WizardOpGate>
        )}
      </div>

      <Spin spinning={isLoading}>
        <Table
          dataSource={modifiers}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Spin>
    </div>
  );
}
